/**
 * ConstructionMilestones — terracotta progress bar with 10 fixed milestones.
 *
 * Two render modes (configured via `variant` prop):
 *   - "compact" (default): the prominent global bar with optional supporting
 *     text rows ("Fase actual", "Última actualización"). Designed to sit
 *     immediately below the hero image as the second thing the investor sees.
 *   - "detailed": the full 10-step grid with per-step state. Designed for the
 *     "Hitos detallados" Section further down.
 *
 * The compact bar is the GENERAL progress of the whole complex: it renders
 * `overallPct` and nothing else. Per-villa milestones never recompute it —
 * they only feed the "detailed" grid. A missing/invalid `overallPct` renders
 * «Pendiente de actualización», not 0%; an explicit 0 renders 0%.
 * `VillaProgressBar` below is the smaller, separate bar for one villa.
 * Never throws.
 */
import React from "react";
import { CheckCircle, Circle, Buildings, Clock } from "@phosphor-icons/react";
import useInvestorLanguage from "@/i18n/useInvestorLanguage";
import { registeredPct } from "@/lib/constructionProgress";

const TERRACOTTA = "#9c5236";
const TERRACOTTA_BG = "#fbf3ee";
const TERRACOTTA_BORDER = "#e4cdbf";

// Static fallback labels used when the admin has not yet wired up the
// backend milestones. Resolved through the i18n dictionary at render time
// so both languages stay in sync.
const FALLBACK_KEYS = [
  "milestones.fallback.1",
  "milestones.fallback.2",
  "milestones.fallback.3",
  "milestones.fallback.4",
  "milestones.fallback.5",
  "milestones.fallback.6",
];

// Backend MILESTONE_DEFAULTS.key → translation key. When `m.key` matches,
// we prefer the translated label over the backend Spanish-only `m.label`.
// Exported so consumers like MyProject can derive the active phase label from
// the persisted 6-step milestones without duplicating this mapping.
export const BACKEND_KEY_TO_TKEY = {
  preparacion: "milestones.key.preparacion",
  cimentacion: "milestones.key.cimentacion",
  estructura:  "milestones.key.estructura",
  cubierta:    "milestones.key.cubierta",
  acabados:    "milestones.key.acabados",
  entrega:     "milestones.key.entrega",
};

// Back-compat for callers importing { MILESTONES }. Spanish source labels
// only; do not use for rendering — use FALLBACK_KEYS through t() instead.
export const MILESTONES = [
  "Preparación",
  "Cimentación",
  "Estructura",
  "Cubierta",
  "Acabados",
  "Entrega",
];

function clampPct(value) {
  const raw = Number(value);
  if (!Number.isFinite(raw)) return 0;
  return Math.max(0, Math.min(100, Math.round(raw)));
}

/**
 * Weighted milestone progress. Each milestone is worth an equal share of the
 * global total (100 / milestones.length). A completed milestone contributes
 * a full share; an in-progress milestone contributes (progress_pct / 100) of
 * a share, defaulting to 50% when progress_pct is missing; a pending
 * milestone contributes 0.
 *
 *   1 completed of 6                         → 17%
 *   2 completed + 1 in_progress @ 50% of 6   → 42%
 *   6 completed                              → 100%
 *
 * Exported so admin ProgresoTab and MyProject use the same formula. The
 * divisor scales with milestones.length so the same helper works when the
 * canonical list grows or shrinks.
 */
export function milestoneGlobalPct(milestones) {
  if (!Array.isArray(milestones) || milestones.length === 0) return 0;
  let sum = 0;
  for (const m of milestones) {
    if (m?.status === "completed") {
      sum += 100;
    } else if (m?.status === "in_progress") {
      const raw = Number(m?.progress_pct);
      sum += Number.isFinite(raw) ? Math.max(0, Math.min(100, raw)) : 50;
    }
  }
  return clampPct(sum / milestones.length);
}

function fmtDate(iso, locale = "es-ES") {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(locale, {
      day: "numeric", month: "short", year: "numeric",
    });
  } catch {
    return null;
  }
}

export default function ConstructionMilestones({
  overallPct = null,
  variant = "compact",
  currentPhaseLabel = null,
  lastUpdateAt = null,
  locale = null,
  globalLabel,
  completedSuffix,
  currentPhaseTitle,
  lastUpdateTitle,
  inProgressLabel,
  stepLabel,
  // PR #7: when the admin has set the 10-step milestones, the investor view
  // uses them as the source of truth. Each item: { key, order, label, status,
  // progress_pct, completed_at }. When absent, fall back to the existing
  // derived-from-pct behavior.
  milestones = null,
}) {
  const { t, lang } = useInvestorLanguage();
  const effectiveLocale = locale || (lang === "en" ? "en-GB" : "es-ES");
  // Default labels resolve through the i18n dictionary so EN/ES stay aligned.
  const labelGlobal      = globalLabel      ?? t("milestones.global_label");
  const labelCompleted   = completedSuffix  ?? t("milestones.completed_suffix");
  const labelPhaseTitle  = currentPhaseTitle ?? t("milestones.current_phase");
  const labelLastUpdate  = lastUpdateTitle  ?? t("milestones.last_update");
  const labelInProgress  = inProgressLabel  ?? t("milestones.in_progress");
  const labelStep        = stepLabel        ?? t("milestones.step");
  const labelPending     = t("milestones.pending_update");

  // Real milestones are used by the detailed grid whenever the backend
  // returns a non-empty list. They never change the compact general bar:
  // that one shows `overallPct`, the complex-wide figure, and only that.
  const hasRealMilestones = Array.isArray(milestones) && milestones.length > 0;
  const fallbackCount = FALLBACK_KEYS.length;
  const stepCount = hasRealMilestones ? milestones.length : fallbackCount;
  const stepSize = stepCount > 0 ? 100 / stepCount : 0;
  const generalPct = registeredPct(overallPct);   // null = pendiente
  const pct = generalPct ?? 0;                    // geometry only
  const derivedCurrentIdx = hasRealMilestones
    ? (() => {
        const inProgress = milestones.findIndex((m) => m.status === "in_progress");
        if (inProgress !== -1) return inProgress;
        const firstPending = milestones.findIndex((m) => m.status !== "completed");
        return firstPending === -1 ? -1 : firstPending;
      })()
    : (generalPct === null || pct >= 100
        ? -1
        : Math.min(fallbackCount - 1, Math.floor(pct / stepSize)));
  const currentIdx = derivedCurrentIdx;
  const fmtLast = fmtDate(lastUpdateAt, effectiveLocale);
  const labels = hasRealMilestones
    ? milestones.map((m) => {
        const tkey = BACKEND_KEY_TO_TKEY[m.key];
        if (tkey) {
          const tr = t(tkey);
          if (tr && tr !== tkey) return tr;
        }
        return m.label;
      })
    : FALLBACK_KEYS.map((k) => t(k));

  if (variant === "detailed") {
    return (
      <ol
        className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2"
        data-testid="construction-milestones-detailed"
      >
        {labels.map((label, i) => {
          const m = hasRealMilestones ? milestones[i] : null;
          const done = m ? m.status === "completed" : pct >= (i + 1) * stepSize;
          const current = !done && (m ? m.status === "in_progress" : i === currentIdx);
          const base = "flex items-start gap-2 rounded border p-2.5 text-xs transition-colors";
          let state = "border-stone-200 bg-white text-stone-500";
          if (done) {
            state = "border-[#e4cdbf] bg-[#fbf3ee] text-stone-700";
          } else if (current) {
            state = "border-[#9c5236] bg-[#fbf3ee] text-stone-900 shadow-sm";
          }
          return (
            <li
              key={i}
              className={`${base} ${state}`}
              data-testid={`milestone-${i + 1}`}
              data-state={done ? "done" : current ? "current" : "upcoming"}
            >
              {done ? (
                <CheckCircle size={16} weight="fill" className="shrink-0 mt-0.5" style={{ color: TERRACOTTA }} />
              ) : (
                <Circle
                  size={16}
                  weight={current ? "bold" : "regular"}
                  className={`shrink-0 mt-0.5 ${current ? "" : "text-stone-300"}`}
                  style={current ? { color: TERRACOTTA } : undefined}
                />
              )}
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-stone-400 mb-0.5">
                  {labelStep} {i + 1}
                </div>
                <div className="leading-tight">{label}</div>
                {current && (
                  <div className="text-[10px] mt-1 font-medium uppercase tracking-wider" style={{ color: TERRACOTTA }}>
                    {labelInProgress}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    );
  }

  // ---------- compact (prominent) variant ----------
  return (
    <div
      className="rounded-lg p-5 sm:p-6 border-2"
      style={{ borderColor: TERRACOTTA_BORDER, background: TERRACOTTA_BG }}
      data-testid="construction-milestones"
    >
      {/* Header — stacks on mobile to avoid collisions on iPhone SE / 13. */}
      <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between sm:gap-4 mb-3">
        <span className="text-[11px] sm:text-xs tracking-widest uppercase text-stone-500">
          {labelGlobal}
        </span>
        {generalPct === null ? (
          <span
            className="font-serif-dv text-xl sm:text-2xl leading-tight"
            style={{ color: TERRACOTTA }}
            data-testid="construction-milestones-pct"
            data-pending="true"
          >
            {labelPending}
          </span>
        ) : (
          <span
            className="font-serif-dv text-3xl sm:text-4xl leading-none"
            style={{ color: TERRACOTTA }}
            data-testid="construction-milestones-pct"
          >
            {generalPct}% <span className="text-base sm:text-lg align-baseline">{labelCompleted}</span>
          </span>
        )}
      </div>

      {/* Terracotta progress bar — thicker than before for prominence. */}
      <div
        className="relative h-4 sm:h-5 bg-white/70 rounded-full overflow-hidden border"
        style={{ borderColor: TERRACOTTA_BORDER }}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        {...(generalPct === null
          ? { "aria-valuetext": labelPending }
          : { "aria-valuenow": generalPct })}
        aria-label={labelGlobal}
      >
        <div
          className="h-full transition-all duration-500"
          style={{ width: `${pct}%`, background: TERRACOTTA }}
        />
      </div>

      {/* Optional supporting text rows — hidden when data missing. */}
      {(currentPhaseLabel || fmtLast) && (
        <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-stone-700">
          {currentPhaseLabel && (
            <div className="inline-flex items-center gap-1.5">
              <Buildings size={14} weight="regular" className="text-stone-500" />
              <span className="text-stone-500">{labelPhaseTitle}:</span>
              <span className="font-medium">{currentPhaseLabel}</span>
            </div>
          )}
          {fmtLast && (
            <div className="inline-flex items-center gap-1.5">
              <Clock size={14} weight="regular" className="text-stone-500" />
              <span className="text-stone-500">{labelLastUpdate}:</span>
              <span className="font-medium">{fmtLast}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * VillaProgressBar — the investor's own villa, under the general bar.
 *
 * Smaller and quieter than the general bar on purpose: same terracotta, a
 * thin track, body-size text. `pct` is the villa's registered percentage or
 * null («Pendiente de actualización»); an explicit 0 renders 0%. Phase and
 * date rows appear only when the villa has them.
 */
export function VillaProgressBar({
  label,
  pct = null,
  phaseLabel = null,
  lastUpdateAt = null,
  note = null,
  locale = null,
}) {
  const { t, lang } = useInvestorLanguage();
  const effectiveLocale = locale || (lang === "en" ? "en-GB" : "es-ES");
  const villaPct = registeredPct(pct);
  const labelPending = t("milestones.pending_update");
  const fmtLast = fmtDate(lastUpdateAt, effectiveLocale);

  return (
    <div
      className="mt-3 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 px-4 py-3 sm:px-5"
      data-testid="villa-progress"
    >
      <div className="flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4 mb-2">
        <span className="text-[11px] tracking-widest uppercase text-stone-500 dark:text-stone-400">
          {label}
        </span>
        <span
          className="text-sm font-medium text-stone-800 dark:text-stone-100"
          data-testid="villa-progress-pct"
          data-pending={villaPct === null ? "true" : undefined}
        >
          {villaPct === null ? labelPending : `${villaPct}% ${t("milestones.completed_suffix")}`}
        </span>
      </div>
      <div
        className="h-2 rounded-full overflow-hidden bg-stone-100 dark:bg-stone-800"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        {...(villaPct === null
          ? { "aria-valuetext": labelPending }
          : { "aria-valuenow": villaPct })}
        aria-label={label}
      >
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${villaPct ?? 0}%`, background: TERRACOTTA, opacity: 0.75 }}
        />
      </div>
      {(phaseLabel || fmtLast) && (
        <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-stone-600 dark:text-stone-300">
          {phaseLabel && (
            <div className="inline-flex items-center gap-1.5" data-testid="villa-progress-phase">
              <Buildings size={12} weight="regular" className="text-stone-400" />
              <span className="text-stone-500 dark:text-stone-400">{t("milestones.current_phase")}:</span>
              <span className="font-medium">{phaseLabel}</span>
            </div>
          )}
          {fmtLast && (
            <div className="inline-flex items-center gap-1.5" data-testid="villa-progress-date">
              <Clock size={12} weight="regular" className="text-stone-400" />
              <span className="text-stone-500 dark:text-stone-400">{t("milestones.last_update")}:</span>
              <span className="font-medium">{fmtLast}</span>
            </div>
          )}
        </div>
      )}
      {note && (
        <div
          className="mt-2 text-xs text-stone-600 dark:text-stone-300 whitespace-pre-line"
          data-testid="villa-progress-note"
        >
          {note}
        </div>
      )}
    </div>
  );
}
